import { get, isEmpty } from 'lodash';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList } from 'react-native';
import { IconSvg } from 'src/assets';
import { SeatProps } from 'src/common/api/response';
import { useSocketSelectSeatMutation } from 'src/common/hooks/socket/useSocketSelectSeatMutation';
import { useSocketUnselectSeatMutation } from 'src/common/hooks/socket/useSocketUnselectSeatMutation';
import { keyExtractor } from 'src/common/utils/KeyExtractor';
import { Container, RegularText, Row, SizedBox } from 'src/components';
import { AppColors } from 'src/themes/Colors';
import { metrics, pixelScaler } from 'src/themes/Metrics';
// import { BusSwitchFloor } from './BusSwitchFloor';
import { SeatItem } from './SeatItem';

export const SeatLayout = ({
  transitId,
  seatLayout,
  passengerSeats,
  setSeatSelected,
  busySeatObj,
  channel,
  totalSeatSelectionCount = 0,
  showIndicator = true,
  viewOnly,
  width = metrics.selectSeatWidth,
}: // limitToSelectSeats = 0,
SeatLayoutProps) => {
  const { t } = useTranslation();

  const [isUserSelected, setUserSelected] = React.useState(false);
  const { mutate: selectSeatMutate } = useSocketSelectSeatMutation(channel);
  const { mutate: unselectSeatMutate } = useSocketUnselectSeatMutation();

  useEffect(() => {
    if (isEmpty(busySeatObj)) return;
    // clear indicator
    const counter = Object.keys(busySeatObj).filter(busySeatKey => {
      return get(busySeatObj, `[${busySeatKey}][0].viewCounter`, 0) !== 0;
    });
    if (counter.length > 0) setUserSelected(true);
    else setUserSelected(false);
  }, [busySeatObj]);

  return (
    <>
      <Container
        width={width}
        style={{
          alignItems: 'center',
          alignSelf: 'center',
          borderWidth: 1,
          borderRadius: metrics.bigBorderRadius,
          borderColor: AppColors.border,
        }}>
        {/* {seatLayout.length > 1 && <BusSwitchFloor data={seatLayout} value={seatLevel} onPress={setSeatLevel} />} */}
        <FlatList
          style={{ flex: 1 }}
          data={seatLayout}
          keyExtractor={keyExtractor}
          showsVerticalScrollIndicator={false}
          renderItem={({ item, index }) => {
            return (
              <FlatList
                data={item}
                horizontal
                showsVerticalScrollIndicator={false}
                contentContainerStyle={{
                  marginBottom: index === 0 ? metrics.large : 0,
                }}
                keyExtractor={keyExtractor}
                renderItem={({ item: seatItem }) => {
                  if (seatItem === null) return <SizedBox width={pixelScaler(50)} />;
                  if (seatItem.type === 'driver')
                    return (
                      <Container style={{ justifyContent: 'flex-end' }}>
                        <IconSvg.Driver />
                      </Container>
                    );

                  if (viewOnly && seatItem.status !== 'available') {
                    return (
                      <Container
                        style={{ justifyContent: 'flex-end' }}
                        paddingTop={pixelScaler(20)}
                        paddingBottom={metrics.small}>
                        <IconSvg.SeatBooked />
                      </Container>
                    );
                  }
                  return (
                    <SeatItem
                      isSelected={passengerSeats.findIndex(seat => seat.id === seatItem.id) !== -1}
                      seat={{
                        ...seatItem,
                        watching: get(busySeatObj, `[${seatItem.label}][0].viewCounter`, 0),
                        transitId,
                      }}
                      disabled={!setSeatSelected}
                      onPress={seatSelected => {
                        if (!setSeatSelected) return;

                        if (passengerSeats.findIndex(seat => seat.id === seatSelected.id) !== -1) {
                          setSeatSelected(passengerSeats.filter(p => p.id !== seatSelected.id));
                          unselectSeatMutate({ itemId: [seatSelected.label], channel });
                          return;
                        }

                        if (totalSeatSelectionCount === 0) {
                          setSeatSelected(passengerSeats.concat(seatSelected));
                          selectSeatMutate(seatSelected.label);
                          return;
                        }

                        // force to check condition
                        if (passengerSeats.length < totalSeatSelectionCount) {
                          setSeatSelected(passengerSeats.concat(seatSelected));
                          selectSeatMutate(seatSelected.label);
                        }
                        // } else if (passengerSeats.length < limitToSelectSeats) {
                        //   setSeatSelected(passengerSeats.concat(seatSelected));
                        //   selectSeatMutate(seatSelected.label);
                        // }
                      }}
                    />
                  );
                }}
              />
            );
          }}
        />
      </Container>
      {showIndicator && isUserSelected ? (
        <Row
          padding={metrics.medium}
          marginTop={metrics.large}
          width={width}
          backgroundColor={AppColors.orange5}
          style={{
            alignSelf: 'center',
            borderWidth: 1,
            borderColor: AppColors.orange,
            borderRadius: metrics.borderRadius,
          }}>
          <IconSvg.Eye />
          <Container flex marginLeft={metrics.small}>
            <RegularText>{t('viewingIndicatorText')}</RegularText>
          </Container>
        </Row>
      ) : (
        <Container />
      )}
    </>
  );
};

interface SeatLayoutProps {
  transitId: string;
  seatLayout?: SeatProps[][];
  passengerSeats: SeatProps[];
  busySeatObj?: any;
  channel: string;
  setSeatSelected?: (seats: SeatProps[]) => void;
  width?: number | string;
  showIndicator?: boolean;
  viewOnly?: boolean;
  totalSeatSelectionCount?: number;
}
